import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, MessageSquare, Calendar, User, Mail } from 'lucide-react'

const FeedbackAdmin = () => {
  const [feedbacks, setFeedbacks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    fetchFeedbacks()
  }, [])

  const fetchFeedbacks = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      setFeedbacks(data || [])
    } catch (error) {
      console.error('Error fetching feedbacks:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString()
  }

  const getFeedbackTypeColor = (type) => {
    return type === 'helpful' ? '#059669' : '#dc2626'
  }

  if (loading) {
    return (
      <div className="App">
        <div className="container" style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>Loading feedback...</h2>
        </div>
      </div>
    )
  }

  return (
    <div className="App">
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ marginBottom: '2rem' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: 'none',
              color: '#0fbab2',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              marginBottom: '1rem'
            }}
          >
            <ChevronLeft size={16} />
            Back to Home
          </button>

          <h1 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>
            <MessageSquare size={24} style={{ display: 'inline', marginRight: '0.5rem' }} />
            Feedback Dashboard
          </h1>
          <p style={{ color: '#64748b' }}>
            Total feedbacks received: {feedbacks.length}
          </p>
        </div>

        {error && (
          <div style={{
            color: '#dc2626',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '2rem'
          }}>
            Error: {error}
          </div>
        )}

        {feedbacks.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            background: '#f8fafc',
            borderRadius: '0.5rem',
            border: '1px solid #e2e8f0'
          }}>
            <MessageSquare size={48} style={{ color: '#94a3b8', marginBottom: '1rem' }} />
            <h3 style={{ color: '#64748b', marginBottom: '0.5rem' }}>No feedback yet</h3>
            <p style={{ color: '#94a3b8' }}>Feedback submissions will appear here.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {feedbacks.map((feedback) => (
              <div
                key={feedback.id}
                style={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div>
                    <h3 style={{
                      color: '#1e293b',
                      marginBottom: '0.25rem',
                      fontSize: '1.1rem'
                    }}>
                      {feedback.care_plan_title}
                    </h3>
                    <p style={{
                      color: '#64748b',
                      fontSize: '0.9rem',
                      margin: '0'
                    }}>
                      Care Plan ID: {feedback.care_plan_id}
                    </p>
                  </div>
                  <span style={{
                    background: `${getFeedbackTypeColor(feedback.feedback_type)}15`,
                    color: getFeedbackTypeColor(feedback.feedback_type),
                    padding: '0.375rem 0.75rem',
                    borderRadius: '0.375rem',
                    fontSize: '0.85rem',
                    fontWeight: '500',
                    textTransform: 'capitalize'
                  }}>
                    {feedback.feedback_type.replace('-', ' ')}
                  </span>
                </div>

                {feedback.comments && (
                  <div style={{
                    background: '#f8fafc',
                    padding: '1rem',
                    borderRadius: '0.375rem',
                    marginBottom: '1rem',
                    border: '1px solid #e2e8f0'
                  }}>
                    <p style={{ margin: '0', color: '#374151', lineHeight: '1.5' }}>
                      "{feedback.comments}"
                    </p>
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.85rem',
                  color: '#64748b',
                  paddingTop: '1rem',
                  borderTop: '1px solid #f1f5f9'
                }}>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    {feedback.user_name && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <User size={14} />
                        {feedback.user_name}
                      </span>
                    )}
                    {feedback.user_email && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Mail size={14} />
                        {feedback.user_email}
                      </span>
                    )}
                  </div>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Calendar size={14} />
                    {formatDate(feedback.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default FeedbackAdmin
